import { LogLevel, PublicClientApplication, type Configuration } from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";

/**
 * Construct a `PublicClientApplication` configured for Authorization Code + PKCE
 * redirect flow.
 *
 * We intentionally do NOT call `initialize()` here — MSAL requires the host
 * application to await initialization before any token API, but that lifecycle
 * belongs to the AuthProvider. Keeping construction and initialization separate
 * makes this helper easy to unit-test without mocking initialization promises.
 */
export function createMsalInstance(config: AppConfig): PublicClientApplication {
    // The deployed PWA lives under a path prefix (e.g. `/einstein/`); MSAL
    // must redirect back to that prefix or Entra will land the auth code on
    // the portal static-site catch-all and the user sees a 404.
    // `import.meta.env.BASE_URL` is exactly the Vite base ('/' in dev, '/einstein/'
    // in deployed builds) so this stays correct under any deployment topology
    // and any future path/sub-domain change without a config edit here.
    const redirectUri = `${window.location.origin}${import.meta.env.BASE_URL}`;
    const configuration: Configuration = {
        auth: {
            clientId: config.msal.clientId,
            authority: config.msal.authority,
            redirectUri,
            postLogoutRedirectUri: redirectUri,
            navigateToLoginRequestUrl: true,
        },
        cache: {
            // sessionStorage is preferred over localStorage: tokens are not persisted
            // beyond the tab lifetime and are not shared across origin tabs, which
            // meaningfully reduces token-theft blast radius.
            cacheLocation: "sessionStorage",
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
                loggerCallback: (level, message) => {
                    if (level === LogLevel.Error) {
                        console.error(`[msal] ${message}`);
                    } else if (level === LogLevel.Warning) {
                        console.warn(`[msal] ${message}`);
                    }
                },
            },
        },
    };

    return new PublicClientApplication(configuration);
}
